import { stripTypename } from "@apollo/client/utilities";
import {
  Callout,
  Card,
  ControlGroup,
  FormGroup,
  InputGroup,
  NumericInput,
  ProgressBar,
  Switch,
} from "@blueprintjs/core";
import {
  getComponentHelperText,
  getComponentIntent,
  ItemSelect,
  ItemSelectButton,
} from "@package/ui-apollo-components";
import { HeaderCreateEdit } from "@package/ui-components";
import {
  CreateLocationDocument,
  DeleteLocationDocument,
  GetLocationDocument,
  GetLocationGridDocument,
  GetLocationListNameIdDocument,
  GetProfileListNameIdDocument,
  type GetProfileListNameIdQuery,
  type GetProfileListNameIdQueryVariables,
  type Location,
  UpdateLocationDocument,
  useGetLocationQuery,
} from "@package/ui-gql/index-data-access";
import { useUpdateSelectedOrganization } from "@package/ui-organization";
import { type ValidationError } from "class-validator";
import { produce, type WritableDraft } from "immer";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  EDIT_CARD_WIDTH_CLASS,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { SaveToolbar } from "../../../SaveToolbar/SaveToolbar";
import { WallPreviewLocation } from "../../../WallPreview/WallPreviewLocation";
import { ProfileCreateDrawer } from "../profile/ProfileEditDrawer";

const defaultLocation: Location = {
  device: {
    appMenu: {
      show: false,
    },
    screens: {
      overscan: {
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
      },
      screenOffset: {
        x: 0,
        y: 0,
      },
      videoSource: {
        enabled: false,
        showCloseButton: true,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
    },
  },
  name: "",
  organization: "",
};

export function LocationEdit() {
  const [locationState, setLocationState] = useState<Location>(defaultLocation);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const { id } = useParams();

  const updateLocation = useCallback(
    (callback: (draft: WritableDraft<Location>) => void) => {
      setLocationState((prev) => produce(prev, callback));
    },
    [],
  );

  const {
    data: dataLocation,
    loading: loadingLocation,
    error: errorLocation,
  } = useGetLocationQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  useEffect(
    function effectOnLocationDataChanged() {
      if (dataLocation?.item) {
        setLocationState(dataLocation.item);
      }
    },
    [dataLocation?.item],
  );

  useUpdateSelectedOrganization(dataLocation?.item?.organization);

  if (loadingLocation) {
    return (
      <Card elevation={1}>
        <ProgressBar intent="primary" stripes animate />
      </Card>
    );
  }

  if (errorLocation) {
    return (
      <Callout intent="danger" title="Failed Loading Location">
        <p>Failed loading location item: {id}</p>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col">
      <HeaderCreateEdit
        editing={!!id}
        loading={loadingLocation}
        title="Location"
        name={dataLocation?.item?.name}
      />
      {!!id && (
        <div className={"flex-0 mt-4"}>
          <WallPreviewLocation location={id} version={locationState._version} />
        </div>
      )}
      <Card elevation={1} className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            label="Name"
            labelFor="control-name"
            labelInfo="(required)"
            disabled={disabled}
            intent={getComponentIntent<Location>("name", errors)}
            helperText={getComponentHelperText<Location>("name", errors)}
          >
            <InputGroup
              disabled={disabled}
              id="control-name"
              placeholder="Name"
              value={locationState.name ?? ""}
              onValueChange={(value) =>
                updateLocation((draft) => {
                  draft.name = value;
                })
              }
            />
          </FormGroup>
          <FormGroup
            disabled={disabled}
            label="Canvas"
            intent={getComponentIntent<Location>("profile", errors)}
            helperText={getComponentHelperText<Location>("profile", errors)}
          >
            <ItemSelect<
              GetProfileListNameIdQuery,
              GetProfileListNameIdQueryVariables
            >
              activeItemId={locationState.profile}
              createDrawerElement={ProfileCreateDrawer}
              disabled={disabled}
              document={GetProfileListNameIdDocument}
              onItemSelected={(id) => {
                updateLocation((draft) => {
                  draft.profile = id;
                });
              }}
            >
              <ItemSelectButton noSelectionText="Select a Canvas" />
            </ItemSelect>
          </FormGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup
            disabled={disabled}
            intent={getComponentIntent("fullscreen", errors)}
            helperText={getComponentHelperText("fullscreen", errors)}
          >
            <Switch
              disabled={disabled}
              label="Fullscreen"
              checked={!!locationState.device?.screens?.fullscreen}
              onChange={() =>
                updateLocation((draft) => {
                  draft.device = {
                    ...draft.device,
                    screens: {
                      ...draft.device?.screens,
                      fullscreen: !draft.device?.screens?.fullscreen,
                    },
                  };
                })
              }
            />
          </FormGroup>
          <ControlGroup fill>
            <FormGroup
              disabled={disabled || !!locationState.device?.screens?.fullscreen}
              label="Offset X"
              intent={getComponentIntent("x", errors)}
              helperText={getComponentHelperText("x", errors)}
            >
              <NumericInput
                disabled={
                  disabled || !!locationState.device?.screens?.fullscreen
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.screenOffset?.x ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        screenOffset: {
                          ...draft.device?.screens?.screenOffset,
                          x: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={disabled || !!locationState.device?.screens?.fullscreen}
              label="Offset Y"
              intent={getComponentIntent("y", errors)}
              helperText={getComponentHelperText("y", errors)}
            >
              <NumericInput
                disabled={
                  disabled || !!locationState.device?.screens?.fullscreen
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.screenOffset?.y ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        screenOffset: {
                          ...draft.device?.screens?.screenOffset,
                          y: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
          </ControlGroup>
          <ControlGroup fill>
            <FormGroup
              disabled={disabled || !!locationState.device?.screens?.fullscreen}
              label="Overscan Left"
              intent={getComponentIntent("left", errors)}
              helperText={getComponentHelperText("left", errors)}
            >
              <NumericInput
                disabled={
                  disabled || !!locationState.device?.screens?.fullscreen
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.overscan?.left ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        overscan: {
                          ...draft.device?.screens?.overscan,
                          left: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={disabled || !!locationState.device?.screens?.fullscreen}
              label="Overscan Right"
              intent={getComponentIntent("right", errors)}
              helperText={getComponentHelperText("right", errors)}
            >
              <NumericInput
                disabled={
                  disabled || !!locationState.device?.screens?.fullscreen
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.overscan?.right ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        overscan: {
                          ...draft.device?.screens?.overscan,
                          right: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={disabled || !!locationState.device?.screens?.fullscreen}
              label="Overscan Top"
              intent={getComponentIntent("top", errors)}
              helperText={getComponentHelperText("top", errors)}
            >
              <NumericInput
                disabled={
                  disabled || !!locationState.device?.screens?.fullscreen
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.overscan?.top ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        overscan: {
                          ...draft.device?.screens?.overscan,
                          top: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={disabled || !!locationState.device?.screens?.fullscreen}
              label="Overscan Bottom"
              intent={getComponentIntent("bottom", errors)}
              helperText={getComponentHelperText("bottom", errors)}
            >
              <NumericInput
                disabled={
                  disabled || !!locationState.device?.screens?.fullscreen
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.overscan?.bottom ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        overscan: {
                          ...draft.device?.screens?.overscan,
                          bottom: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
          </ControlGroup>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
          <FormGroup disabled={disabled}>
            <Switch
              disabled={disabled}
              label="Video Input Enabled"
              checked={!!locationState.device?.screens?.videoSource?.enabled}
              onChange={() =>
                updateLocation((draft) => {
                  draft.device = {
                    ...draft.device,
                    screens: {
                      ...draft.device?.screens,
                      videoSource: {
                        ...draft.device?.screens?.videoSource,
                        enabled: !draft.device?.screens?.videoSource?.enabled,
                      },
                    },
                  };
                })
              }
            />
          </FormGroup>
          <FormGroup disabled={disabled}>
            <Switch
              disabled={disabled}
              label="Show Close Button on Video Input"
              checked={
                !!locationState.device?.screens?.videoSource?.showCloseButton
              }
              onChange={() =>
                updateLocation((draft) => {
                  draft.device = {
                    ...draft.device,
                    screens: {
                      ...draft.device?.screens,
                      videoSource: {
                        ...draft.device?.screens?.videoSource,
                        showCloseButton:
                          !draft.device?.screens?.videoSource?.showCloseButton,
                      },
                    },
                  };
                })
              }
            />
          </FormGroup>
          <ControlGroup fill>
            <FormGroup
              disabled={
                disabled || !locationState.device?.screens?.videoSource?.enabled
              }
              label="Offset X"
            >
              <NumericInput
                disabled={
                  disabled ||
                  !locationState.device?.screens?.videoSource?.enabled
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.videoSource?.x ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        videoSource: {
                          ...draft.device?.screens?.videoSource,
                          x: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={
                disabled || !locationState.device?.screens?.videoSource?.enabled
              }
              label="Offset Y"
            >
              <NumericInput
                disabled={
                  disabled ||
                  !locationState.device?.screens?.videoSource?.enabled
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.videoSource?.y ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        videoSource: {
                          ...draft.device?.screens?.videoSource,
                          y: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={
                disabled || !locationState.device?.screens?.videoSource?.enabled
              }
              label="Width"
            >
              <NumericInput
                disabled={
                  disabled ||
                  !locationState.device?.screens?.videoSource?.enabled
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.videoSource?.width ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        videoSource: {
                          ...draft.device?.screens?.videoSource,
                          width: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
            <FormGroup
              className="ml-4"
              disabled={
                disabled || !locationState.device?.screens?.videoSource?.enabled
              }
              label="Height"
            >
              <NumericInput
                disabled={
                  disabled ||
                  !locationState.device?.screens?.videoSource?.enabled
                }
                min={0}
                allowNumericCharactersOnly
                fill
                value={locationState.device?.screens?.videoSource?.height ?? 0}
                onValueChange={(value) => {
                  updateLocation((draft) => {
                    draft.device = {
                      ...draft.device,
                      screens: {
                        ...draft.device?.screens,
                        videoSource: {
                          ...draft.device?.screens?.videoSource,
                          height: value,
                        },
                      },
                    };
                  });
                }}
              />
            </FormGroup>
          </ControlGroup>
          <Callout intent="none" icon="help">
            Video Inputs are external video sources physically connected to the
            device. The x/y coordinates and the width/height are set in pixels.
          </Callout>
        </div>
      </Card>
      <Card className="mt-4">
        <div className={`flex ${EDIT_CARD_WIDTH_CLASS}`}>
          <SaveToolbar
            documentCreate={CreateLocationDocument}
            documentDelete={DeleteLocationDocument}
            documentUpdate={UpdateLocationDocument}
            path={SidebarEntryPath.locations}
            isNew={!id}
            refetchQueries={[
              GetLocationDocument,
              GetLocationGridDocument,
              GetLocationListNameIdDocument,
            ]}
            title="Location"
            validationModel="location-update"
            variablesCreate={stripTypename(locationState)}
            variablesUpdate={stripTypename(locationState)}
            variablesDelete={{ id }}
            onInflight={(inflight) => {
              setDisabled(inflight);

              if (inflight) {
                setErrors([]);
              }
            }}
            onValidationFailed={(errors) => {
              setErrors(errors);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
